import {
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query'
import { useApiWithIDToken } from '../../state/modules/common'
import { useNotifications } from '../../state/modules/notifications'
import { Variant } from '../../common/types'
import { useProgressToast } from './helpers/useProgressToast'

type ReviewVariables = {
  documentId: number
  documentName: string
  verify: boolean
  comment?: string
}

/**
 * Review the file(s) uploaded for a given checklist document.
 *
 * @returns A UseMutationResult object for triggering the review.
 */
export const useReviewChecklistDocument = (): UseMutationResult<
  void,
  Error,
  ReviewVariables
> => {
  const api = useApiWithIDToken()
  const queryClient = useQueryClient()
  const { send: sendNotification } = useNotifications()
  const { startProgress, endProgress } = useProgressToast()

  return useMutation<void, Error, ReviewVariables, { toastId: string }>({
    mutationFn: ({ documentId, documentName, verify, comment }) =>
      reviewChecklistDocument(api, documentId, documentName, verify, comment),
    onMutate: (vars) => ({
      toastId: startProgress(
        'Adding Review',
        `Adding review for ${vars.documentName}`
      ),
    }),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({
        queryKey: ['siteRFPChecklists'],
        refetchType: 'active',
      })
      void queryClient.invalidateQueries({
        queryKey: ['siteRFPDocumentSummary'],
        refetchType: 'active',
      })
      sendNotification({
        title: 'Review Added',
        message: `Review for ${vars.documentName} has been successfully added.`,
        useRucksackUI: true,
      })
    },
    onError: (_data, vars) => {
      sendNotification({
        title: 'Review Failed',
        message: `An error occurred while trying to add a review for ${vars.documentName}. Please try again.`,
        variant: Variant.DANGER,
        useRucksackUI: true,
      })
    },
    onSettled: (_data, _error, _vars, context) => {
      endProgress(context?.toastId)
    },
  })
}

async function reviewChecklistDocument(
  api: ReturnType<typeof useApiWithIDToken>,
  documentId: number,
  documentName: string,
  verify: boolean,
  comment?: string
): Promise<void> {
  try {
    const url = `/documents/${documentId}/review`
    await api.post(url, { comment, verify })
  } catch (error) {
    throw new Error(
      `Failed to save review for document ${documentName}: ${
        error instanceof Error ? error.message : 'Unknown error'
      }`
    )
  }
}
